import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';

const DataDeletion = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingRequest, setExistingRequest] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkExistingRequest();
  }, []);

  const checkExistingRequest = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Redirect to auth page with return path
        navigate('/auth?returnTo=/data-deletion');
        return;
      }

      const { data, error } = await supabase
        .from('deletion_requests')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('status', 'pending')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setExistingRequest(!!data);
      if (data) {
        setEmail(session.user.email || '');
      }
    } catch (error) {
      console.error('Error checking existing request:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to submit a deletion request');
        navigate('/auth');
        return;
      }

      // Validate email format
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Please enter a valid email address');
        return;
      }

      // Check if email matches the authenticated user's email
      if (email !== session.user.email) {
        toast.error('The email must match your account email');
        return;
      }

      const { error } = await supabase
        .from('deletion_requests')
        .insert({
          user_id: session.user.id,
          email,
          reason: reason.trim(),
          status: 'pending'
        });

      if (error) throw error;

      toast.success('Your data deletion request has been submitted. We will process it within 30 days.');
      setExistingRequest(true);
    } catch (error) {
      console.error('Error submitting deletion request:', error);
      toast.error('Failed to submit deletion request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0B1A]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-3xl font-bold text-white mb-8">Data Deletion Request</h1>
        
        <div className="space-y-8 text-gray-300">
          {existingRequest ? (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-4">Request Pending</h2>
              <p className="text-gray-300">
                Your data deletion request is currently being processed. We will handle your request within 30 days.
                You will receive an email confirmation once your data has been deleted.
              </p>
            </div>
          ) : (
            <>
              <section>
                <h2 className="text-xl font-semibold text-white mb-4">About Data Deletion</h2>
                <p className="mb-4">
                  In accordance with data protection regulations, you have the right to request the deletion
                  of your personal data from our systems. Please note:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Deletion requests are typically processed within 30 days</li>
                  <li>Some data may be retained for legal compliance purposes</li>
                  <li>Deletion is permanent and cannot be undone</li>
                  <li>Active subscriptions must be cancelled before data deletion</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-4">Submit a Deletion Request</h2>
                <div className="bg-[#151632]/50 backdrop-blur-sm p-6 rounded-xl border border-[#1E2048]">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full bg-[#0A0B1A] border border-[#1E2048] rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                        placeholder="Enter your account email"
                      />
                      <p className="mt-1 text-sm text-gray-400">
                        Must match your account email address
                      </p>
                    </div>

                    <div>
                      <label htmlFor="reason" className="block text-sm font-medium text-gray-300 mb-1">
                        Reason for Deletion (Optional)
                      </label>
                      <textarea
                        id="reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={4}
                        className="w-full bg-[#0A0B1A] border border-[#1E2048] rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                        placeholder="Please let us know why you're requesting data deletion"
                      />
                    </div>

                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                        <p className="text-sm text-yellow-200">
                          By submitting this request, you understand that this action is permanent and cannot be undone.
                          All your data will be permanently deleted from our systems.
                        </p>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || !email}
                      className="w-full bg-red-500 text-white py-2.5 rounded-lg font-semibold hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-[#0A0B1A] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                      ) : (
                        'Request Data Deletion'
                      )}
                    </button>
                  </form>
                </div>
              </section>
            </>
          )}

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Alternative Methods</h2>
            <p className="mb-4">
              You can also request data deletion by:
            </p>
            <div className="bg-[#151632]/50 backdrop-blur-sm p-6 rounded-xl border border-[#1E2048]">
              <p>Email: info@unifyads.io</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion;